"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useTokenStore } from "@/lib/zustand";
import { ChainId, ChainType, ChainKey } from "@lifi/sdk";
import { SwapTokensForm } from "@/components/forms/swap-tokens-form";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { UserSettingsModal } from "@/components/modals/user-settings-modal";
import { getToken } from "@/hooks/get-tokens-and-chains";
import { useAccount as useParaAccount, useModal as useParaModal, useWallet as useParaWallet } from "@getpara/react-sdk";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import { useAccount as useWagmiAccount } from "wagmi";
import { WidgetWalletProvider } from "@/contexts/widget-wallet-context";

/**
 * Widget connect button with dual wallet options:
 * - Email Wallet: Opens Para modal for email/social login
 * - External Wallet: Opens wallet-adapter modal for Phantom/Solflare via bridge
 */
function WidgetConnectButton() {
  const { openModal: openParaModal } = useParaModal();
  const { isConnected: isParaConnected } = useParaAccount();
  const { data: paraWallet } = useParaWallet();
  const { publicKey: solanaPublicKey, disconnect: disconnectSolana, connected: isSolanaConnected } = useSolanaWallet();
  const { setVisible: openWalletModal } = useWalletModal();
  const { address: wagmiAddress } = useWagmiAccount();
  const { fromChain } = useTokenStore();

  const isSolana = fromChain?.chainType === ChainType.SVM;

  // Check which connection type is active
  const hasParaSession = isParaConnected && !!paraWallet?.address;
  const hasExternalWallet = isSolanaConnected && !!solanaPublicKey;

  // Derive display address based on connection type
  const displayAddress = hasExternalWallet
    ? solanaPublicKey?.toBase58()
    : hasParaSession && paraWallet?.type === "SOLANA"
      ? paraWallet.address
      : isSolana
        ? null
        : (paraWallet?.type === "EVM" ? paraWallet.address : null) ?? wagmiAddress ?? null;

  const showConnectedState = hasExternalWallet || hasParaSession;

  // Connected state - show address with click to manage
  if (showConnectedState && displayAddress) {
    const handleClick = () => {
      if (hasExternalWallet) {
        // External wallet - disconnect
        disconnectSolana();
      } else {
        // Para wallet - open modal to manage
        openParaModal();
      }
    };

    return (
      <button
        onClick={handleClick}
        title={hasExternalWallet ? "Click to disconnect" : "Click to manage wallet"}
        style={{
          padding: "6px 12px",
          fontSize: 12,
          fontWeight: 600,
          background: "linear-gradient(135deg, #f9cd4f 0%, #d4a012 100%)",
          color: "#111",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        {displayAddress.slice(0, 4)}...{displayAddress.slice(-4)}
      </button>
    );
  }

  // Disconnected state - show dual connect options
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <button
        onClick={() => openParaModal()}
        title="Connect with email or social login"
        style={{
          padding: "6px 10px",
          fontSize: 11,
          fontWeight: 600,
          background: "linear-gradient(135deg, #f9cd4f 0%, #d4a012 100%)",
          color: "#111",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Email
      </button>
      <button
        onClick={() => openWalletModal(true)}
        title="Connect Phantom or Solflare"
        style={{
          padding: "6px 10px",
          fontSize: 11,
          fontWeight: 600,
          background: "transparent",
          color: "#f9cd4f",
          border: "1px solid #f9cd4f",
          borderRadius: 8,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Wallet
      </button>
    </div>
  );
}

// LiFi's Solana chain ID
const SOLANA_CHAIN_ID = 1151111081099710;

// Native SOL address
const SOL_NATIVE_ADDRESS = "11111111111111111111111111111111";

// Default SOL token
const DEFAULT_SOL_TOKEN = {
  chainId: ChainId.SOL,
  address: SOL_NATIVE_ADDRESS,
  symbol: "SOL",
  name: "Solana",
  decimals: 9,
  logoURI: "https://assets.coingecko.com/coins/images/4128/small/solana.png?1640133422",
  priceUSD: "0",
};

// Default Solana chain
const SOLANA_CHAIN = {
  id: ChainId.SOL,
  name: "Solana",
  chainType: ChainType.SVM,
  logoURI: "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg",
  symbol: ChainKey.SOL,
};

export default function WidgetPage() {
  const searchParams = useSearchParams();
  const { setFromToken, setToToken, setFromChain, setToChain } = useTokenStore();
  const [initialized, setInitialized] = useState(false);

  // Parse URL params and pre-select tokens
  useEffect(() => {
    async function initializeTokens() {
      const toTokenParam = searchParams.get("toToken");
      const fromTokenParam = searchParams.get("fromToken");

      // Handle fromToken - defaults to SOL
      if (fromTokenParam && fromTokenParam.toUpperCase() !== "SOL") {
        // Try to fetch the fromToken if it's not SOL
        try {
          const token = await getToken(ChainId.SOL, fromTokenParam);
          if (token) {
            setFromToken(token);
            setFromChain(SOLANA_CHAIN);
          }
        } catch (e) {
          console.warn("[Widget] Failed to fetch fromToken:", fromTokenParam, e);
          // Fall back to SOL
          setFromToken(DEFAULT_SOL_TOKEN);
          setFromChain(SOLANA_CHAIN);
        }
      } else {
        // Default to SOL
        setFromToken(DEFAULT_SOL_TOKEN);
        setFromChain(SOLANA_CHAIN);
      }

      // Handle toToken - pre-select if provided
      if (toTokenParam) {
        try {
          // Assume Solana chain for now (most common use case for launchpad embeds)
          const token = await getToken(ChainId.SOL, toTokenParam);
          if (token) {
            setToToken(token);
            setToChain(SOLANA_CHAIN);
          }
        } catch (e) {
          console.warn("[Widget] Failed to fetch toToken:", toTokenParam, e);
          // Don't crash - just don't pre-select
        }
      }

      setInitialized(true);
    }

    initializeTokens();
  }, [searchParams, setFromToken, setToToken, setFromChain, setToChain]);

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        background: "#111",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div style={{ width: "100%", maxWidth: 440 }}>
        <Card
          style={{
            border: "1px solid rgba(255,255,255,0.07)",
            borderTop: "1px solid rgba(255,255,255,0.13)",
            background:
              "linear-gradient(180deg,rgba(22,22,26,0.95) 0%,rgba(16,16,20,0.95) 100%)",
            backdropFilter: "blur(40px)",
            borderRadius: 20,
            boxShadow:
              "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "10%",
              right: "10%",
              top: 0,
              height: 1,
              background:
                "linear-gradient(90deg,transparent,rgba(249,205,79,0.15),transparent)",
            }}
          />
          <CardHeader className="flex flex-row items-center justify-between gap-3 p-4 sm:p-6 pb-2">
            <div style={{ color: "#f9cd4f", fontWeight: 600, fontSize: 15 }}>
              Swap Tokens
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <WidgetConnectButton />
              <UserSettingsModal />
            </div>
          </CardHeader>
          <CardContent className="pt-2 px-4 sm:px-6 pb-6">
            <WidgetWalletProvider>
              <SwapTokensForm />
            </WidgetWalletProvider>
          </CardContent>
        </Card>
        <div
          style={{
            textAlign: "center",
            marginTop: 12,
            fontSize: 11,
            color: "#555",
          }}
        >
          Powered by{" "}
          <a
            href="https://forgeswap.fun"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#f9cd4f", textDecoration: "none" }}
          >
            ForgeSwap
          </a>
        </div>
      </div>
    </div>
  );
}
